// SpaceEnergyTab.tsx
import React, { useState } from "react";
import { useLaneCopy } from "../../../../utils/laneCopy";
import { Home } from "lucide-react";
import AstriaVietnamShell, {
  SHELL_COLORS_ASTRIA_VIETNAM,
} from "../AstriaVietnamShell";
import {
  AstriaVietnamResultCard,
  AstriaVietnamFieldBlock,
  AstriaVietnamLoadingState,
  AstriaVietnamErrorState,
  AstriaVietnamChatCta,
} from "../AstriaVietnamCard";
import {
  WizardStep,
  WizardNav,
  WizardDropdown,
} from "../AstriaVietnamWizard";
import { useAstriaVietnamTab } from "../useAstriaVietnamTab";
import { AstriaVietnamSpaceEnergyData } from "../../../../contexts/ChatContext";
import { useAstriaVietnamLanguage } from "../astriaVietnamLanguage";
import { ASTRIA_VIETNAM_COPY } from "../astriaVietnamCopy";

// SubCategory name must contain "Space Energy" — matches
// resolveVietnamSubcategoryEntry in
// backend/helper/vietnam/astriaVietnamPromptService.js.
const SUBCATEGORY_NAME = "Space Energy";

const SpaceEnergyTab: React.FC = () => {
  const lang = useAstriaVietnamLanguage();
  const copy = useLaneCopy("vn", ASTRIA_VIETNAM_COPY);
  const t = copy.spaceEnergy;
  const shared = copy.shared;

  const { loading, error, message, send, reset, goToChat } =
    useAstriaVietnamTab({ subCategoryName: SUBCATEGORY_NAME, language: lang });

  const [spaceType, setSpaceType] = useState<string | null>(null);
  const [direction, setDirection] = useState<string | null>(null);

  const started = !!message || loading || !!error;
  const step1Valid = !!spaceType;

  const handleSubmit = () => {
    send("What is the energy of this space, and how can I balance it?", {
      space_type: spaceType || undefined,
      direction: direction || undefined,
    });
  };

  const spaceEnergyData = message?.astriaVietnamData as
    | AstriaVietnamSpaceEnergyData
    | undefined;

  if (started) {
    return (
      <AstriaVietnamShell active="space-energy" lang={lang}>
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: SHELL_COLORS_ASTRIA_VIETNAM.titleColor,
              marginBottom: 4,
            }}
          >
            {t.tabLabel}
          </div>
          <p style={{ fontSize: 13.5, color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor }}>
            {t.tabSublabel} — {t.resultTitle}
          </p>
        </div>

        {loading && <AstriaVietnamLoadingState label={t.loading} />}

        {!loading && error && (
          <AstriaVietnamErrorState
            message={error}
            onRetry={handleSubmit}
            retryLabel={shared.tryAgain}
          />
        )}

        {!loading && !error && spaceEnergyData && (
          <>
            <AstriaVietnamResultCard
              icon={<Home className="w-5 h-5" />}
              title={t.resultTitle}
              subtitle={t.tabLabel}
              shareText={`${t.resultTitle}\n\n${spaceEnergyData.space_reading}\n\n${spaceEnergyData.guidance}`}
            >
              <AstriaVietnamFieldBlock label={t.fields.spaceReading} value={spaceEnergyData.space_reading} />
              <AstriaVietnamFieldBlock label={t.fields.directionNote} value={spaceEnergyData.direction_note} />
              <AstriaVietnamFieldBlock label={t.fields.energySuggestion} value={spaceEnergyData.energy_suggestion} />
              <AstriaVietnamFieldBlock label={t.fields.guidance} value={spaceEnergyData.guidance} />
            </AstriaVietnamResultCard>
            <button
              onClick={() => {
                reset();
                setSpaceType(null);
                setDirection(null);
              }}
              className="text-xs font-semibold transition-colors"
              style={{ marginTop: 14, color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor }}
            >
              {shared.startNewReading}
            </button>
            <AstriaVietnamChatCta onClick={goToChat} label={shared.continueInChat} />
          </>
        )}

        {!loading && !error && !spaceEnergyData && message && (
          <AstriaVietnamResultCard
            icon={<Home className="w-5 h-5" />}
            title={t.resultTitle}
            subtitle={t.tabLabel}
          >
            <p
              style={{
                fontSize: 15,
                lineHeight: 1.75,
                color: SHELL_COLORS_ASTRIA_VIETNAM.bodyColor,
                whiteSpace: "pre-wrap",
              }}
            >
              {message.content}
            </p>
          </AstriaVietnamResultCard>
        )}
      </AstriaVietnamShell>
    );
  }

  return (
    <AstriaVietnamShell active="space-energy" lang={lang}>
      <WizardStep stepKey="details" question={t.step1Question}>
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
              marginBottom: 8,
            }}
          >
            {t.spaceTypeLabel}
          </div>
          <WizardDropdown
            options={t.spaceTypeOptions}
            value={spaceType}
            onChange={setSpaceType}
            placeholder={t.spaceTypePlaceholder}
          />
        </div>

        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
              marginBottom: 8,
            }}
          >
            {t.directionLabel}
          </div>
          <WizardDropdown
            options={t.directionOptions}
            value={direction}
            onChange={setDirection}
            placeholder={t.directionPlaceholder}
          />
        </div>

        <WizardNav
          showBack={false}
          nextDisabled={!step1Valid}
          onNext={handleSubmit}
          nextLabel={t.revealButton}
          backLabel={shared.back}
        />
      </WizardStep>
    </AstriaVietnamShell>
  );
};

export default SpaceEnergyTab;
